// publish-87 round-11 · item B — TikTok results for the connected account.
//
// Live fetch on open + Refresh button (no cached snapshot):
//   * follower / following / likes / video counts  (user.info.stats)
//   * recent videos with views, likes, comments, shares (video.list)
// GET /publish/tiktok/results?connection_id=…

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function Stat({ label, value, testId }) {
  return (
    <div className="bo-card" style={{ display: "grid", gap: 4, padding: 14 }} data-testid={testId}>
      <span className="bo-eyebrow">{label}</span>
      <span style={{ fontSize: 26, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtNum(value)}</span>
    </div>
  );
}

export default function TikTokResults() {
  const [connections, setConnections] = useState(null);
  const [connectionId, setConnectionId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/publish/channel-connections?channel=tiktok")
      .then((r) => {
        if (!alive) return;
        const rows = (r.data?.connections || []).filter((c) => c.status !== "revoked");
        setConnections(rows);
        if (rows.length >= 1) setConnectionId(rows[0].id);
      })
      .catch((e) => { if (alive) { setConnections([]); setError(formatApiError(e)); } });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/publish/tiktok/results?connection_id=${encodeURIComponent(cid)}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(connectionId); }, [connectionId, load]);

  const acct = data?.account;
  const errs = data?.errors || {};

  return (
    <Shell title="TikTok results">
      <div data-testid="tiktok-results-page" style={{ display: "grid", gap: 16, maxWidth: 1100 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="bo-field" style={{ minWidth: 260 }}>
            <label className="bo-label" htmlFor="tiktok-results-connection">Account</label>
            <select id="tiktok-results-connection" className="bo-input" value={connectionId}
                    onChange={(e) => setConnectionId(e.target.value)} data-testid="tiktok-results-connection-select">
              <option value="">Choose…</option>
              {(connections || []).map((c) => <option key={c.id} value={c.id}>{c.display_name || c.id}</option>)}
            </select>
          </div>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={() => load(connectionId)}
                  disabled={!connectionId || loading} data-testid="tiktok-results-refresh">
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          {data?.fetched_at && (
            <span style={{ fontSize: 12, color: "var(--bo-muted)" }} data-testid="tiktok-results-fetched-at">
              Fetched live from TikTok at {fmtWhen(data.fetched_at)}
            </span>
          )}
          <span style={{ marginLeft: "auto" }}>
            <Link to="/brand-os/publish/tiktok" data-testid="tiktok-results-open-post">Post to TikTok →</Link>
          </span>
        </div>

        <ErrorBanner error={error} testId="tiktok-results-error" />
        {connections !== null && connections.length === 0 && (
          <div className="bo-card" data-testid="tiktok-results-no-connection" style={{ color: "var(--bo-muted)" }}>
            No TikTok account is connected for this business yet.{" "}
            <Link to="/brand-os/admin/channel-connections">Connect TikTok →</Link>
          </div>
        )}
        {loading && !data && <LoadingCard label="Fetching account stats and recent videos from TikTok…" />}

        {data && (
          <>
            <section style={{ display: "grid", gap: 12 }} data-testid="tiktok-results-account">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {acct?.avatar_url && (
                  <img src={acct.avatar_url} alt={`${acct.display_name || "Account"} avatar`} width={48} height={48}
                       style={{ borderRadius: "50%", objectFit: "cover" }} />
                )}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }} data-testid="tiktok-results-display-name">
                    {acct?.display_name || data.display_name || "TikTok account"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                    scopes: {(data.granted_scopes || []).join(", ") || "—"}
                  </div>
                </div>
              </div>
              {errs.account ? (
                <div className="bo-card" data-testid="tiktok-results-account-error" style={{ fontSize: 13 }}>
                  <Pill tone="warn">Stats unavailable</Pill>{" "}
                  {errs.account.message}
                  {errs.account.needs_scope && <> — reconnect TikTok and grant <code>{errs.account.needs_scope}</code>.</>}
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
                  <Stat label="Followers" value={acct?.follower_count} testId="tiktok-stat-followers" />
                  <Stat label="Following" value={acct?.following_count} testId="tiktok-stat-following" />
                  <Stat label="Likes" value={acct?.likes_count} testId="tiktok-stat-likes" />
                  <Stat label="Videos" value={acct?.video_count} testId="tiktok-stat-videos" />
                </div>
              )}
            </section>

            <section className="bo-card" data-testid="tiktok-results-videos" style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: 17 }}>Recent videos</h2>
                <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>last {data.videos?.length || 0} from video.list</span>
              </div>
              {errs.videos ? (
                <div data-testid="tiktok-results-videos-error" style={{ fontSize: 13 }}>
                  <Pill tone="warn">Videos unavailable</Pill>{" "}
                  {errs.videos.message}
                  {errs.videos.needs_scope && <> — reconnect TikTok and grant <code>{errs.videos.needs_scope}</code>.</>}
                </div>
              ) : (data.videos || []).length === 0 ? (
                <div style={{ color: "var(--bo-muted)", fontSize: 13 }} data-testid="tiktok-results-videos-empty">
                  No videos on this account yet.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="bo-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "var(--bo-muted)" }}>
                        <th style={{ padding: "6px 8px" }}>Video</th>
                        <th style={{ padding: "6px 8px" }}>Posted</th>
                        <th style={{ padding: "6px 8px", textAlign: "right" }}>Views</th>
                        <th style={{ padding: "6px 8px", textAlign: "right" }}>Likes</th>
                        <th style={{ padding: "6px 8px", textAlign: "right" }}>Comments</th>
                        <th style={{ padding: "6px 8px", textAlign: "right" }}>Shares</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.videos.map((v) => (
                        <tr key={v.id} data-testid={`tiktok-video-row-${v.id}`} style={{ borderTop: "1px solid var(--bo-line)" }}>
                          <td style={{ padding: "8px" }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                              {v.cover_image_url && (
                                <img src={v.cover_image_url} alt="" width={36} height={64}
                                     style={{ objectFit: "cover", borderRadius: 6 }} />
                              )}
                              <div style={{ display: "grid" }}>
                                <span style={{ fontWeight: 600 }}>{v.title || "(no caption)"}</span>
                                {v.share_url && <a href={v.share_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Open on TikTok →</a>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "8px", whiteSpace: "nowrap" }}>{fmtWhen(v.create_time)}</td>
                          <td style={{ padding: "8px", textAlign: "right" }}>{fmtNum(v.view_count)}</td>
                          <td style={{ padding: "8px", textAlign: "right" }}>{fmtNum(v.like_count)}</td>
                          <td style={{ padding: "8px", textAlign: "right" }}>{fmtNum(v.comment_count)}</td>
                          <td style={{ padding: "8px", textAlign: "right" }}>{fmtNum(v.share_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </Shell>
  );
}
